import { useQuery } from '@apollo/client';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import UserPost from '../../../components/Post/UserPost.component';
import { useAuth } from '../../../context/AuthContext';
import { POST_REPORTS } from '../../../graphql/resolvers/moderation/postReports.gql';
import ReportSidebar from './ReportSidebar';
import VerdictBar from './VerdictBar';
import '../moderation.scss';

interface Props {
  postId: string | null;
  onClose: () => void;
}

export default function ReportModal({ postId, onClose }: Props) {
  const { user } = useAuth();
  const currentUserRole = (user as { role?: string } | null)?.role;
  const { data, loading, error } = useQuery(POST_REPORTS, {
    variables: { postId: postId as string },
    skip: !postId,
    fetchPolicy: 'cache-and-network',
  });

  if (!postId) return null;

  return (
    <Dialog.Root
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="Moderation__Modal__Overlay" />
        <Dialog.Content className="Moderation__Modal__Content">
          <Dialog.Title className="Moderation__Modal__Title">
            Report details
          </Dialog.Title>
          <Dialog.Description className="Moderation__Modal__Description">
            Reported post and all open reports against it.
          </Dialog.Description>
          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close"
              className="Moderation__Modal__Close"
            >
              <X size={16} />
            </button>
          </Dialog.Close>

          {loading && (
            <div className="Moderation__Modal__Loading">Loading…</div>
          )}
          {error && (
            <div className="Moderation__Modal__Error">
              Couldn't load this report. It may have been resolved or deleted.
            </div>
          )}
          {data?.postReports && (
            <div className="Moderation__Modal__Body">
              <div className="Moderation__Modal__Post">
                {data.postReports.post ? (
                  <UserPost
                    post={data.postReports.post}
                    postReplyDraftId={undefined}
                    username={data.postReports.post.username ?? ''}
                    status={undefined}
                  />
                ) : (
                  <div
                    className="Moderation__Modal__PostTombstone"
                    data-testid="post-tombstone"
                  >
                    <div
                      className="Moderation__Modal__PostTombstone__Icon"
                      aria-hidden
                    >
                      ⌫
                    </div>
                    <h2>Post unavailable</h2>
                    <p>
                      The reported post has been deleted by its author or
                      removed by a prior moderation action. The reports below
                      are preserved so you can still record a verdict.
                    </p>
                  </div>
                )}
              </div>
              <ReportSidebar reports={data.postReports.reports} />
            </div>
          )}
          {data?.postReports &&
            (currentUserRole === 'moderator' ||
              currentUserRole === 'admin') && (
              <VerdictBar
                reports={data.postReports.reports}
                postId={postId as string}
                currentUserRole={currentUserRole as 'moderator' | 'admin'}
                onResolved={() => {
                  onClose();
                }}
              />
            )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
